import React from "react";
import "./InputField.scss";
import { InputFieldProps } from "./InputFieldProps";
import { Config } from "../../../Config/Config";
import { TextField } from "@mui/material";
import { TextFieldEndIcon } from "../../Booking/Widget/TextFieldEndIcon";
import { GoogleAddressPicker } from "../../AddressPicker/Components/GoogleAddressPicker";
import { LocationTypes } from "../../AddressPicker/Entities/PlaceSearchEntities";

/** Custom component for all the input fields of the contact form. */
class InputField extends React.Component<InputFieldProps, {}> {
    
    state = {
        isFocus: false
    };

    /** Get the type of the input field */
    private get fieldType(): string {
        const type = this.props.type;
        if (!type || type === "text") {
            return "text";
        }
        if ( type === "phone") {
            return "tel";
        }
        return type;
    }

    /** Check if the input field is for a phone input to add additional elements and to decide the length. */
    private get isPhone(): boolean {
        return this.props.type === "phone";
    }

    /** Check if the input is an address field to make it a Google place autocomplete type field */
    private get isAddress(): boolean {
        return this.props.type === "address";
    }

    /** Add international code and the flag to (in front of) the input field. Currently, it is hard coded to australia. */
    private get intlCode(): JSX.Element | null {
        return this.isPhone ?
            <div className="contact-input-group-phone">
                <label className="input-label phone-label">{this.props.labelForPhoneNumber}</label>
                <div className="phone-input">
                    <img src={this.props.imgSrc} className="flag-icon" alt="logo" />
                    <div className="intlcode">+61</div>
                </div>
            </div> : null;
    }

    public render() {
        return <div className="input-container">
            {this.intlCode}
            <div className="contact-input-group">
                {this.isAddress ? this.getAddressInput() : this.getStandardInput()}
            </div>
        </div>;
    }

    /** Get text/number input field with the given properties. */
    private getStandardInput = () => {

        // Validate the input
        const isInputInvalid = !this.state.isFocus && !!this.props.isError;
        
        // Show clear text icon, if the input field has valid value and it's not in focussed
        const canShowClearTextIcon = !isInputInvalid && !this.state.isFocus && !!this.props.value;

        const contactNumberLabelAlignmentCssClass = this.isPhone ? "contact-number-label-alignment" : "";

        return (
            <TextField
                fullWidth={true}
                variant="outlined"
                id={this.props.name}
                type={this.fieldType}
                error={isInputInvalid}
                name={this.props.name}
                value={this.props.value}
                label={this.props.label}
                onChange={this.props.updateHandler}
                placeholder={this.props.placeholder}
                onFocus={() => this.updateFocusStatus(true)}
                onBlur={() => this.updateFocusStatus(false)}
                className={contactNumberLabelAlignmentCssClass}
                inputProps={{ maxLength: this.isPhone ? Config.ContactNumberMaxLength : undefined }}
                InputProps={{
                    endAdornment: <TextFieldEndIcon showClearTextIcon={canShowClearTextIcon} showErrorIcon={isInputInvalid} onClick={this.onClearEvent} />
                }}
            />
        );
    }

    /** Assign empty value to input field on click of the cleartext icon */
    onClearEvent = () => {
        if (this.props.clearTextHandler) this.props.clearTextHandler(this.props.name);
    }
    
    /** Update the focus state of the text field */
    updateFocusStatus = (isFocus: boolean) => this.setState({ isFocus: isFocus });
    
    /** Get address inputs field. Reusing GoogleAddressPicker component here. */
    private getAddressInput = () => {

        return (
            <GoogleAddressPicker
                LocationType={LocationTypes.AddressStrict}
                OnPlaceSelected={this.addressSelected}
                LabelText={this.props.label}
                PreferNearbyTo={this.props.location}
                SpecifiedPlaceText={this.props.value}
                OnCleared={this.props.onCleared}
                AutoFocus={false}
                IncludeFavouriteAddresses={false}
            />
        );
    }

    private addressSelected = (place: google.maps.places.PlaceResult, displayAddress: string) => {
        if (this.props.onAddressChosen) {
            this.props.onAddressChosen(displayAddress);
        }
    }
}

export default InputField;